import { createSelector } from '@ngrx/store'
import { RootState } from '../root.model'
import { FocusedRedux } from './focused.reducer'

const focusedState = (state: RootState) => state.focused

export const selectFocusedAccounts = createSelector(
  focusedState,
  (state: FocusedRedux) => state.accountIds,
)

/**
 * DLITE-4110: the account (from Accounts-panel selection) whose comments the
 * drawer should display. Null when the selection isn't commentary-eligible.
 */
export const selectFocusedCommentaryAccountId = createSelector(
  focusedState,
  (state: FocusedRedux) => state.commentaryAccountId ?? null,
)

export const selectFocusedTotalBreaks = createSelector(
  focusedState,
  (state: FocusedRedux) => state.totalAccountBreaks,
)

export const selectFocusedAccountsAndTotalBreaks = createSelector(
  focusedState,
  (state: FocusedRedux) => ({
    totalBreaks: state.totalAccountBreaks,
    accountIds: state.accountIds,
  }),
)

/**
 * Selects the first account of the array or returns undefined.
 * We do need undefined to pass through the pipe.
 */
export const selectFirstFocusedAccount = createSelector(
  focusedState,
  (state: FocusedRedux) => {
    if (state.accountIds.length > 0) {
      return state.accountIds[0]
    }

    return undefined
  },
)

export const selectFirstFocusedAccountInfo = createSelector(
  focusedState,
  (state: FocusedRedux) => {
    if (state.accountInfo.length > 0) {
      return state.accountInfo[0]
    }

    return undefined
  },
)

export const selectFocusedBreak = createSelector(
  focusedState,
  (state: FocusedRedux) => state.break,
)

export const selectFocusedCorporateActionIssue = createSelector(
  focusedState,
  (state: FocusedRedux) => state.corporateActionIssueId,
)

export const selectFocusedCWTransaction = createSelector(
  focusedState,
  (state: FocusedRedux) => state.cwTranId,
)

export const selectFocusedCWTransactions = createSelector(
  focusedState,
  (state: FocusedRedux) => state.cwTranIds,
)

export const selectFocusedExtTransaction = createSelector(
  focusedState,
  (state: FocusedRedux) => state.extTranId,
)

export const selectFocusedExtTransactions = createSelector(
  focusedState,
  (state: FocusedRedux) => state.extTranIds,
)

export const selectFocusedPositionSecurity = createSelector(
  focusedState,
  (state: FocusedRedux) => state.positionSecurityId,
)

export const selectFocusedAssetId = createSelector(
  focusedState,
  (state: FocusedRedux) => state.assetId,
)

export const selectFocusedSecurities = createSelector(
  focusedState,
  (state: FocusedRedux) => state.securityIds,
)

export const selectFocusedUIComponent = createSelector(
  focusedState,
  (state: FocusedRedux) => state.uiComponentName,
)

export const selectFocusedUIKey = createSelector(
  focusedState,
  (state: FocusedRedux) => state.uiComponentKey,
)
export const selectFocusedExternalFileFilter = createSelector(
  focusedState,
  (state: FocusedRedux) => state.externalFileFilter,
)

export const selectFocusedSecurityMappingFilter = createSelector(
  focusedState,
  (state: FocusedRedux) => state.securityMappingFilter,
)

export const selectFocusedJobs = createSelector(
  focusedState,
  (state: FocusedRedux) => state.jobs,
)

export const selectFocusedApprovalsAccountId = createSelector(
  focusedState,
  (state: FocusedRedux) => state.approvalAccountIds,
)

/**
 * If ONE security is focused, this selects it.  If many or none are selected, returns undefined.
 * We do need undefined to pass through the pipe.
 */
export const selectSingleFocusedSecurity = createSelector(
  focusedState,
  (state: FocusedRedux) => {
    if (state.securityIds.length === 1) {
      return state.securityIds[0]
    }

    return undefined
  },
)

export const selectSubSleeveAccountId = createSelector(
  focusedState,
  (state: FocusedRedux) => {
    if (state.subSleeveAccountId) {
      return state.subSleeveAccountId
    }
    return null
  },
)

export const selectFocusedBulkCwTransactions = createSelector(
  focusedState,
  (state: FocusedRedux) => state.bulkCwTranIds,
)
export const selectFocusedBulkExtTransactions = createSelector(
  focusedState,
  (state: FocusedRedux) => state.bulkExtTranIds,
)

export const selectFocusedLegacyTranIds = createSelector(
  focusedState,
  (state: FocusedRedux) => state.legacyTranIds,
)

export const selectFocusedLegacyOrigIds = createSelector(
  focusedState,
  (state: FocusedRedux) => state.legacyOrigIds,
)

export const selectFocusedExtPosId = createSelector(
  focusedState,
  (state: FocusedRedux) => state.extPosId,
)
